/**
 * <MacroRates> — 10년물 국채금리 (US10Y) + VIX 변동성
 *
 * 매크로 1번지표: 금리 = 시장 방향, VIX = 변동성/공포
 * 데이터: GET /api/ticker/market/rates → { tnx: {...}, vixy: {...} }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface RateQuote {
  symbol: string;
  label: string;
  description: string;
  price: number | null;
  changePercent: number | null;
  change: number | null;
}

interface RatesData {
  tnx: RateQuote | null;
  vixy: RateQuote | null;
  spread: RateQuote | null;  // 10Y - 2Y 스프레드
}

const REFRESH_MS = 2 * 60 * 1000;

function fmtPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}

function fmtPrice(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function Row({ q, suffix = "" }: { q: RateQuote | null; suffix?: string }) {
  const pct = q?.changePercent ?? null;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  return (
    <div className="dw-rates-row">
      <div className="dw-rates-meta">
        <div className="dw-rates-label">{q?.label || "—"}</div>
        <div className="dw-rates-desc">{q?.description || ""}</div>
      </div>
      <div className="dw-rates-vals">
        <div className="dw-rates-price">
          {fmtPrice(q?.price ?? null)}
          {suffix && <span className="dw-rates-suffix">{suffix}</span>}
        </div>
        <div className={`dw-rates-pct ${up ? "up" : down ? "down" : ""}`}>{fmtPct(pct)}</div>
      </div>
    </div>
  );
}

export function MacroRates(_props: WidgetProps) {
  const [data, setData] = useState<RatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/rates", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-rates">
      <div className="dw-head"><span className="dw-title">금리 · 변동성</span></div>
      {error ? (
        <div className="dw-rates-error">{error}</div>
      ) : loading && !data ? (
        <div className="dw-rates-empty">로딩 중…</div>
      ) : (
        <div className="dw-rates-list">
          <Row q={data?.tnx ?? null} suffix="%" />
          <Row q={data?.spread ?? null} suffix="bp" />
          <Row q={data?.vixy ?? null} />
        </div>
      )}
    </div>
  );
}

export const macroRatesMeta: WidgetMeta = {
  type: "macro-rates",
  name: "금리·변동성",
  description: "10년물 국채금리 + VIX · 매크로 1번지표",
  icon: "📊",
  category: "market",
  defaultSize: { w: 3, h: 3 },
  availableSizes: [
    { w: 3, h: 3 },
    { w: 4, h: 3 },
    { w: 3, h: 4 },
  ],
  minW: 2,
  maxW: 6,
  minH: 2,
  maxH: 5,
  component: MacroRates,
};
